// =============================================
// MATERIALS LIBRARY
// PBR materials + textbook color legend
// =============================================
import * as THREE from 'three';

// ===== TEXTBOOK COLOR LEGEND =====
// Matches Avotek / Jeppesen diagram conventions
export const TEXTBOOK_COLORS = {
    impactAir:      0x90CAF9,  // light blue
    venturiSuction: 0x1565C0,  // darker blue
    fuelSupply:     0xFFEE58,  // yellow
    regulatedFuel:  0xFFA726,  // orange
    meteredFuel:    0xEF5350,  // red
};

export const SYSTEM_HEX = {
    air:      '#90CAF9',
    fuel:     '#EF5350',
    mixture:  '#FFA726',
    linkage:  '#FDD835',
    control:  '#66BB6A',
    sensor:   '#AB47BC',
};

// ===== CACHED MATERIAL INSTANCES =====
const _cache = {};

function cached(key, factory) {
    if (!_cache[key]) _cache[key] = factory();
    return _cache[key].clone();
}

// ===== METAL MATERIALS =====

/** Satin aluminum housing with premium 'Mica' finish for hologram effect */
export function housingMaterial() {
    return cached('housing', () => new THREE.MeshPhysicalMaterial({
        color: 0x94A3B8,
        metalness: 0.15,
        roughness: 0.35,      // Increased for 'Frosted' look
        transparent: true,
        opacity: 0.98,
        transmission: 0.15,   // More 'Glassy' than just transparent
        thickness: 4.0,       // Thick edges for diffraction
        ior: 1.45,
        clearcoat: 1.0,
        clearcoatRoughness: 0.05,
        envMapIntensity: 1.5,
        emissive: 0x0F172A,
        emissiveIntensity: 0.05,
        side: THREE.DoubleSide
    }));
}

/** Darker steel for internal structural parts */
export function steelMaterial() {
    return cached('steel', () => new THREE.MeshPhysicalMaterial({
        color: 0x6B7B8D,
        metalness: 0.88,
        roughness: 0.3,
        clearcoat: 0.08,
        clearcoatRoughness: 0.35,
        envMapIntensity: 0.9,
        emissive: 0x0A1020,
        emissiveIntensity: 0.01,
    }));
}

/** Brass for screws, mixture needle, idle mixture screw */
export function brassMaterial() {
    return cached('brass', () => new THREE.MeshPhysicalMaterial({
        color: 0xC8A84E,
        metalness: 0.9,
        roughness: 0.28,
        clearcoat: 0.1,
        clearcoatRoughness: 0.3,
        envMapIntensity: 1.0,
        emissive: 0x3A2A05,
        emissiveIntensity: 0.03,
    }));
}

/** Polished steel for shafts and precision parts */
export function shaftMaterial() {
    return cached('shaft', () => new THREE.MeshPhysicalMaterial({
        color: 0x9EAAB8,
        metalness: 0.92,
        roughness: 0.2,
        clearcoat: 0.15,
        clearcoatRoughness: 0.2,
        envMapIntensity: 1.0,
    }));
}

// ===== FLUID MATERIALS =====

/** Raw fuel supply — translucent yellow */
export function fuelSupplyMaterial() {
    return cached('fuelSupply', () => new THREE.MeshPhysicalMaterial({
        color: TEXTBOOK_COLORS.fuelSupply,
        metalness: 0.0,
        roughness: 0.15,
        transparent: true,
        opacity: 0.55,
        transmission: 0.3,
        thickness: 0.5,
        emissive: new THREE.Color(TEXTBOOK_COLORS.fuelSupply),
        emissiveIntensity: 0.15,
        side: THREE.DoubleSide,
        depthWrite: false,
    }));
}

/** Regulated fuel — translucent orange */
export function regulatedFuelMaterial() {
    return cached('regulatedFuel', () => new THREE.MeshPhysicalMaterial({
        color: TEXTBOOK_COLORS.regulatedFuel,
        metalness: 0.0,
        roughness: 0.15,
        transparent: true,
        opacity: 0.6,
        transmission: 0.25,
        thickness: 0.5,
        emissive: new THREE.Color(TEXTBOOK_COLORS.regulatedFuel),
        emissiveIntensity: 0.15,
        side: THREE.DoubleSide,
        depthWrite: false,
    }));
}

/** Metered fuel to discharge nozzle — translucent red */
export function meteredFuelMaterial() {
    return cached('meteredFuel', () => new THREE.MeshPhysicalMaterial({
        color: TEXTBOOK_COLORS.meteredFuel,
        metalness: 0.0,
        roughness: 0.15,
        transparent: true,
        opacity: 0.65,
        transmission: 0.2,
        thickness: 0.5,
        emissive: new THREE.Color(TEXTBOOK_COLORS.meteredFuel),
        emissiveIntensity: 0.18,
        side: THREE.DoubleSide,
        depthWrite: false,
    }));
}

// ===== AIR MATERIALS =====

/** Impact air volume — faint light blue */
export function impactAirMaterial() {
    return cached('impactAir', () => new THREE.MeshPhysicalMaterial({
        color: TEXTBOOK_COLORS.impactAir,
        metalness: 0.0,
        roughness: 0.1,
        transparent: true,
        opacity: 0.12,
        emissive: new THREE.Color(TEXTBOOK_COLORS.impactAir),
        emissiveIntensity: 0.08,
        side: THREE.DoubleSide,
        depthWrite: false,
    }));
}

/** Venturi suction air — darker blue volume */
export function venturiSuctionMaterial() {
    return cached('venturiSuction', () => new THREE.MeshPhysicalMaterial({
        color: TEXTBOOK_COLORS.venturiSuction,
        metalness: 0.0,
        roughness: 0.1,
        transparent: true,
        opacity: 0.18,
        emissive: new THREE.Color(TEXTBOOK_COLORS.venturiSuction),
        emissiveIntensity: 0.1,
        side: THREE.DoubleSide,
        depthWrite: false,
    }));
}

// ===== UTILITY MATERIALS =====

/** Rubber gasket / diaphragm */
export function rubberMaterial() {
    return cached('rubber', () => new THREE.MeshStandardMaterial({
        color: 0x2A2A2A,
        metalness: 0.05,
        roughness: 0.85,
    }));
}

/** Ghost/faded material for system isolation */
export function ghostMaterial() {
    return cached('ghost', () => new THREE.MeshStandardMaterial({
        color: 0x444444,
        metalness: 0.1,
        roughness: 0.8,
        transparent: true,
        opacity: 0.08,
        depthWrite: false,
    }));
}

/** Get a passage material by textbook passage type */
export function getPassageMaterial(passageType) {
    switch (passageType) {
        case 'impactAir':      return impactAirMaterial();
        case 'venturiSuction': return venturiSuctionMaterial();
        case 'fuelSupply':     return fuelSupplyMaterial();
        case 'regulatedFuel':  return regulatedFuelMaterial();
        case 'meteredFuel':    return meteredFuelMaterial();
        default:               return steelMaterial();
    }
}
